import { Add } from '@mui/icons-material';
import { Button, Stack } from '@mui/material';
import { t } from 'i18next';
import { FormEventHandler } from 'react';
import { useFormItem } from '../../common/hooks/use-form-item/index';
import { InputType } from '../../common/hooks/use-form-item/types';
import { Slot } from '../models/Slot';
import { SlotType } from '../models/SlotType';
import { ButtonBox, PrimaryButton, TertiaryButton } from './Buttons';
import { SlotForm } from './SlotForm';

type Props = {
  slots: Slot[];
  submit: {
    label: string;
    action: (slotId: number) => void;
  };
  createSlot: {
    label: string;
    action: (name: string, description: string, slotType: string, tags: string[]) => Promise<void>;
  };
  back: {
    label: string;
    action: () => void;
  };
  slotTypes: SlotType[];
};

const CREATE_SLOT_VALUE = 'CREATE_SLOT_VALUE';

const CreateSlotOption = () => {
  return (
    <Button color="light-grey" variant="contained" startIcon={<Add />} sx={{ justifyContent: 'start' }} disableElevation fullWidth>
      {t(`slotSelectionForm.create`)}
    </Button>
  );
};

export const SlotSelectionForm = ({ slots, submit, createSlot, back, slotTypes }: Props) => {
  const [slotId, slotComponent] = useFormItem<number | 'CREATE_SLOT_VALUE' | null>({
    type: InputType.TYPE_AUTOCOMPLETE,
    label: t(`slotSelectionForm.choose`),
    value: null,
    validator: null,
    inputOptions: {
      autoFocus: true,
      focusedLabel: t('slot'),
    },
    autocompleteOptions: {
      options: [
        { value: CREATE_SLOT_VALUE, label: t(`slotSelectionForm.create`), element: <CreateSlotOption /> },
        ...slots.map((s) => ({ value: s.getId(), label: s.getName() })),
      ],
    },
  });

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    if (!slotId || slotId === CREATE_SLOT_VALUE) return;

    submit.action(slotId);
  };

  const isCreatingSlot = slotId === CREATE_SLOT_VALUE;

  return (
    <Stack spacing={1.5} component={isCreatingSlot ? 'div' : 'form'} onSubmit={isCreatingSlot ? undefined : onSubmit}>
      {slotComponent}
      {isCreatingSlot && <SlotForm submit={createSlot} back={back} slotTypes={slotTypes} />}
      {!isCreatingSlot && (
        <ButtonBox>
          <TertiaryButton onClick={back.action}>{back.label}</TertiaryButton>
          <PrimaryButton disabled={!slotId} type="submit">
            {submit.label}
          </PrimaryButton>
        </ButtonBox>
      )}
    </Stack>
  );
};
